import * as Sentry from "@sentry/nextjs";
import { isCalInvalidAccessTokenError } from "@/lib/errors/cal-access-token";
import { timeAmount } from "../time";
import { withRetry } from "../utils/retry";
import { CAL_API_URL, CAL_API_VERSION_DEFAULT } from "./constants";
import { isRetryableCalTransientError } from "./transient-errors";
import { CalApiError, CalApiRateLimitError } from "./types";

/** Attempts beyond the first for a replayable request that hit a transient Cal/gateway fault. */
const MAX_TRANSIENT_RETRIES = 2;

export interface CalRequestAlertContext {
  calCredentialsId?: string;
  memberId?: string;
  practiceId?: string;
  providerId?: string;
  roomId?: string;
}

type CalRequestOptions = RequestInit & {
  alertContext?: CalRequestAlertContext;
  calApiVersion?: string;
  shouldCaptureInvalidAccessToken?: boolean;
};

/**
 * A Cal error body, split by whether it actually parsed as JSON. Cal v2 answers with a JSON error
 * payload; a body that isn't JSON came from something in front of Cal and is treated as opaque.
 */
type CalErrorBody =
  | { kind: "json"; payload: unknown }
  | { kind: "opaque"; body: string }
  | { kind: "empty" };

async function parseCalErrorBody(response: Response): Promise<CalErrorBody> {
  const rawBody = await response.text().catch(() => "");
  if (!rawBody) {
    return { kind: "empty" };
  }

  try {
    return { kind: "json", payload: JSON.parse(rawBody) as unknown };
  } catch {
    return { kind: "opaque", body: rawBody };
  }
}

/** Cloudflare error pages run to kilobytes — keep enough to identify one, not the whole document. */
const OPAQUE_BODY_DETAIL_LIMIT = 500;

function buildCalError({
  errorBody,
  statusCode,
}: {
  errorBody: CalErrorBody;
  statusCode: number;
}): Error {
  // A non-JSON body means a layer in front of Cal answered — a Cloudflare 5xx interstitial, say.
  // Using it as the message puts a whole HTML page into Error.message, which then becomes the
  // Sentry issue title (`Error: <!DOCTYPE html>`) and can surface in a user-facing toast. Keep it
  // as attached detail and describe the failure by its status instead.
  if (errorBody.kind === "opaque") {
    const details = errorBody.body.slice(0, OPAQUE_BODY_DETAIL_LIMIT);
    return statusCode === 429
      ? new CalApiRateLimitError("Too many requests", details)
      : new CalApiError(`Cal API request failed with status ${statusCode}`, statusCode, details);
  }

  const payload = errorBody.kind === "json" ? errorBody.payload : undefined;
  return normalizeCalError({ error: CalApiError.parse(payload, statusCode), statusCode });
}

function normalizeCalError({ error, statusCode }: { error: unknown; statusCode: number }): Error {
  if (error instanceof Error) {
    return error;
  }

  if (statusCode === 429) {
    return new CalApiRateLimitError(
      typeof error === "string" && error.length > 0 ? error : "Too many requests",
    );
  }

  if (typeof error === "string" && error.length > 0) {
    return new CalApiError(error, statusCode);
  }

  return new CalApiError(`Cal API request failed with status ${statusCode}`, statusCode);
}

/**
 * Fetches a resource from the Cal.com API.
 *
 * @param endpoint The endpoint to fetch.
 * @param options The request options.
 * @returns The response body.
 * @throws CalApiRateLimitError if the rate limit is exceeded.
 * @throws CalApiError if the request fails.
 */
export async function fetchFromCalWithRetry<T>(
  endpoint: string,
  options: CalRequestOptions = {},
): Promise<T> {
  // Two budgets share one retry loop. Rate limits keep the long one (Cal wants us to back off for
  // minutes). Transient gateway faults get a much tighter one: these calls sit inside user-facing
  // requests, and a 524 already cost Cal ~100s, so retrying for minutes would hang the page.
  let transientRetries = 0;

  const response = await withRetry(() => fetchFromCal<T>(endpoint, options), {
    shouldRetry: (error) => {
      if (CalApiRateLimitError.isInstance(error)) {
        return true;
      }

      if (!isRetryableCalTransientError({ error, method: options.method })) {
        return false;
      }

      transientRetries += 1;
      return transientRetries <= MAX_TRANSIENT_RETRIES;
    },
    delay: timeAmount(1, "second"),
    maxDelay: timeAmount(5, "minutes"),
  });
  return response;
}

async function fetchFromCal<T>(endpoint: string, options: CalRequestOptions = {}): Promise<T> {
  const {
    alertContext: callerAlertContext,
    calApiVersion,
    shouldCaptureInvalidAccessToken = true,
    ...requestOptions
  } = options;
  // Build and validate the final URL using the URL API to avoid spoofing via string tricks
  // such as userinfo (e.g., https://api.cal.com@evil.com/) or similar host/path confusions.
  const base = new URL(CAL_API_URL.endsWith("/") ? CAL_API_URL : `${CAL_API_URL}/`);

  // Resolve candidate URL using normalized endpoint to avoid duplicating base path.
  const normalized = normalizeCalEndpoint(base, endpoint);
  const candidate = new URL(normalized, base);

  // Enforce same origin as Cal API
  if (candidate.origin !== base.origin) {
    throw new Error(`fetchFromCal received a URL for a non-Cal origin: ${endpoint}`);
  }

  // Enforce that the path stays within the allowed base path (e.g., /v2)
  // Normalize to ensure trailing slash on base.pathname for prefix check.
  const basePath = base.pathname.endsWith("/") ? base.pathname : `${base.pathname}/`;
  const candidatePath = candidate.pathname;
  const basePathNoSlash = basePath.slice(0, -1);
  if (!candidatePath.startsWith(basePath) && candidatePath !== basePathNoSlash) {
    throw new Error(`fetchFromCal received a URL outside allowed base path: ${endpoint}`);
  }

  const fullUrl = candidate.toString();

  const response = await fetch(fullUrl, {
    ...requestOptions,
    headers: {
      "Content-Type": "application/json",
      "cal-api-version": calApiVersion ?? CAL_API_VERSION_DEFAULT,
      ...requestOptions.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await parseCalErrorBody(response);
    const normalizedError = buildCalError({ errorBody, statusCode: response.status });

    if (isCalInvalidAccessTokenError(normalizedError) && shouldCaptureInvalidAccessToken) {
      const alertContext = {
        endpoint: candidate.pathname,
        statusCode: response.status,
        calApiVersion: calApiVersion ?? CAL_API_VERSION_DEFAULT,
        message: normalizedError.message,
        ...callerAlertContext,
      };
      // Only Sentry capture here — logger.error would mirror to console and get re-captured
      // by captureConsoleIntegration as "[object Object]". Sentry.withScope sets proper tags/context.
      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setFingerprint(["cal-auth-invalid-access-token"]);
        scope.setTag("cal.auth_error", "invalid_access_token");
        scope.setTag("cal.endpoint", candidate.pathname);
        if (callerAlertContext?.memberId) {
          scope.setTag("vibrant.memberId", callerAlertContext.memberId);
        }
        if (callerAlertContext?.practiceId) {
          scope.setTag("vibrant.practiceId", callerAlertContext.practiceId);
        }
        if (callerAlertContext?.providerId) {
          scope.setTag("vibrant.providerId", callerAlertContext.providerId);
        }
        if (callerAlertContext?.roomId) {
          scope.setTag("roomId", callerAlertContext.roomId);
        }
        scope.setContext("cal", alertContext);
        Sentry.captureMessage("cal-auth-invalid-access-token");
      });
    }

    throw normalizedError;
  }
  return response.json();
}

/**
 * Returns true if the provided URL string is absolute (starts with http/https).
 */
function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Normalizes an endpoint so that it resolves cleanly against the Cal API base without
 * duplicating the base path (e.g., avoiding /v2/v2/...) and while accepting common forms:
 *   - "users" → relative to base ("/v2/users")
 *   - "/users" → relative to base ("/v2/users")
 *   - "v2/users" or "/v2/users" → treated as already prefixed once
 * For absolute URLs, the caller must still validate origin and base path.
 */
function normalizeCalEndpoint(base: URL, endpoint: string): string {
  const ep = endpoint.trim();
  const basePath = base.pathname.endsWith("/") ? base.pathname : `${base.pathname}/`; // e.g. "/v2/"
  const basePathNoSlash = basePath.slice(0, -1); // e.g. "/v2"
  const baseSegment = basePathNoSlash.slice(1); // e.g. "v2"

  if (isAbsoluteUrl(ep)) {
    return ep;
  }

  // Handle paths that begin with '/'
  if (ep.startsWith("/")) {
    // If it's already under the correct base ("/v2" or "/v2/...") keep as root-based
    if (ep === basePathNoSlash || ep.startsWith(basePath)) {
      return ep;
    }
    // Otherwise, make it relative to the base by removing the leading slash
    return ep.slice(1);
  }

  // Handle relative forms that redundantly include the base segment (e.g., "v2/users")
  if (ep === baseSegment) {
    // Empty relative path resolves to base ("/v2/")
    return "";
  }
  if (ep.startsWith(`${baseSegment}/`)) {
    return ep.slice(baseSegment.length + 1);
  }

  // Simple relative path (e.g., "users")
  return ep;
}
